import { useEffect, useRef } from 'react'

/**
 * Écoute la douchette code-barres.
 *
 * Une douchette USB ou Bluetooth se présente au système comme un clavier : elle
 * « tape » la référence en quelques millisecondes puis envoie Entrée. On repère
 * donc une lecture à sa cadence — des frappes espacées de moins de 60 ms — pour
 * ne pas confondre un scan avec un vendeur qui écrit dans un champ de recherche.
 */
export function useScanner(surCode: (code: string) => void, actif = true): void {
  const tampon = useRef('')
  const derniereFrappe = useRef(0)
  const rappel = useRef(surCode)
  rappel.current = surCode

  useEffect(() => {
    if (!actif) return

    const surTouche = (evenement: KeyboardEvent) => {
      const cible = evenement.target as HTMLElement | null
      const dansUnChamp =
        cible instanceof HTMLInputElement ||
        cible instanceof HTMLTextAreaElement ||
        cible instanceof HTMLSelectElement

      const maintenant = performance.now()
      const rapide = maintenant - derniereFrappe.current < 60
      derniereFrappe.current = maintenant

      if (evenement.key === 'Enter') {
        const code = tampon.current
        tampon.current = ''
        // Une référence fait au moins trois caractères : en deçà c'est une
        // validation de formulaire, pas une lecture d'étiquette.
        if (code.length >= 3) {
          evenement.preventDefault()
          rappel.current(code)
        }
        return
      }

      if (evenement.key.length !== 1 || evenement.ctrlKey || evenement.metaKey || evenement.altKey) {
        return
      }

      // Hors cadence de douchette, on repart de zéro : c'est de la frappe humaine.
      if (!rapide) tampon.current = ''
      // Dans un champ de saisie, seule une vraie rafale est interceptée.
      if (dansUnChamp && !rapide && tampon.current === '') return

      tampon.current += evenement.key
    }

    window.addEventListener('keydown', surTouche, true)
    return () => window.removeEventListener('keydown', surTouche, true)
  }, [actif])
}
